import Swiper from "./swiper-bundle.esm.browser.min.js";
import PhotoSwipeLightbox from "./photoswipe-lightbox.esm.min.js";

const badges = document.querySelector(".photoswipe > .card__badges");

const lightbox = new PhotoSwipeLightbox({
  // may select multiple "galleries"
  gallery: ".photoswipe",

  // Elements within gallery (slides)
  children: "a",

  // setup PhotoSwipe Core dynamic import
  pswpModule: () => import("./photoswipe.esm.min.js"),

  bgOpacity: 1,
});
lightbox.init();
lightbox.on("beforeOpen", () => {
  badges?.classList.add("hide");
});

lightbox.on("destroy", () => {
  badges?.classList.remove("hide");
});

if (!customElements.get("gallery-section")) {
  customElements.define(
    "gallery-section",
    class GallerySection extends HTMLElement {
      updateSwiperAutoHeight = () => {
        if (this.resolution != this.matchResolution()) {
          this.resolution = this.matchResolution();
          this.destroyCarouselGallery();
          this.initializeGallery();
          this.setStickyGallery();
        }
      };

      constructor() {
        super();
      }

      connectedCallback() {
        this.isProductPage = this.dataset.productPage === "";

        this.galleryLoader = this.querySelector("#gallery-loader");
        this.renderedSlides = [];
        this.readConfiguration();
        this.initializeGallery();
        this.galleryLoader?.classList.add("hidden");
        this.classList.remove("loading");
        this.classList.add("loaded");

        this.setStickyGallery();
      }

      disconnectedCallback() {
        // this.destroyGallery();
      }

      setStickyGallery() {
        if (this.isProductPage) {
          const headerHeight =
            document.querySelector(".wt-header")?.offsetHeight;
          const swiperRect = this.getBoundingClientRect();

          const fitsInViewport =
            swiperRect.top >= 0 &&
            swiperRect.left >= 0 &&
            swiperRect.height <= window.innerHeight;

          const positionTopValue = `${headerHeight + 16}px`;

          this.style.setProperty("--position-top", positionTopValue);
          this.classList.add("wt-product__gallery--sticky", fitsInViewport);
        }
      }

      readConfiguration() {
        // general configuration
        this.configuration = [];

        this.elements = {
          section: this,
          gallery: this.querySelector("[data-gallery]"),
          thumbs: this.querySelector("[data-thumbs]"),
          galleryContainer: this.querySelector("[data-gallery]").querySelector(
            "[data-swiper-container]",
          ),
          thumbsContainer: this.querySelector("[data-thumbs]").querySelector(
            "[data-swiper-container]",
          ),
        };

        this.elements.gallerySlides = Array.from(
          this.elements.gallery.querySelectorAll("[data-swiper-slide]"),
        ).map((e) => e.cloneNode(true));
        this.elements.thumbsSlides = Array.from(
          this.elements.thumbs.querySelectorAll("[data-swiper-slide]"),
        ).map((e) => e.cloneNode(true));

        // Cache plus-tagged media once — avoid re-parsing alts on every variant change.
        this.plusMediaIds = new Set();
        this.elements.gallerySlides.forEach((slide) => {
          if (slide.getAttribute("data-plus-size") === "true") {
            const mediaId =
              slide.getAttribute("data-media-id") ||
              slide.getAttribute("data-slide-media-id") ||
              slide.querySelector("img, video")?.getAttribute("data-media-id");
            if (mediaId) this.plusMediaIds.add(String(mediaId));
            return;
          }
          const media =
            slide.querySelector("img") ||
            slide.querySelector("video") ||
            null;
          const alt =
            media?.getAttribute("alt") ||
            slide.getAttribute("data-alt") ||
            "";
          if (!this.altHasPlusTag(alt)) return;
          const mediaId =
            media?.getAttribute("data-media-id") ||
            slide.getAttribute("data-media-id") ||
            slide.getAttribute("data-slide-media-id");
          if (mediaId) this.plusMediaIds.add(String(mediaId));
        });
        this.hasPlusMedia = this.plusMediaIds.size > 0;

        const default_configuration = {
          sliderEnabledBreakpoint: 900,
          desktopLayout: "carousel-vertical",
        };

        const custom_configuration = JSON.parse(
          this.elements.section.querySelector("[data-configuration]").innerHTML,
        );
        this.configuration = {
          ...default_configuration,
          ...custom_configuration,
        };

        let autoHeightEnabled = window.innerWidth <= 768;

        const UNMUTE_CLASS_TOGGLE = "wt-hero-video__sound-toggle--unmuted";

        function handleSlideVideos(swiper) {
          const allVideos = Array.from(swiper.slides).flatMap((slide) =>
            Array.from(slide.querySelectorAll("video")),
          );
          const allToggleBtns = Array.from(swiper.slides).flatMap((slide) =>
            Array.from(slide.querySelectorAll(".wt-hero-video__sound-toggle")),
          );

          // pause, mute & disable controls for all videos
          allVideos.forEach((video) => {
            video.pause();
            video.currentTime = 0;
            video.muted = true;
            video.controls = false;
            clearTimeout(video._hideCtrlsTimer);
          });

          // reset all toggle buttons to "off" state
          allToggleBtns.forEach((btn) => {
            btn.setAttribute("data-sound", "off");
            btn.classList.remove(UNMUTE_CLASS_TOGGLE);
          });

          // play & keep muted the active slide's video
          const activeSlide = swiper.slides[swiper.activeIndex];
          const activeVideo = activeSlide?.querySelector("video");
          if (activeVideo) {
            activeVideo.muted = true;
            activeVideo.play().catch((err) => {
              console.warn("Video playback failed:", err);
            });
          }
        }

        function initVideoInteractions(swiper) {
          handleSlideVideos(swiper);

          // add delegation listener for clicks
          swiper.el.addEventListener("click", (e) => {
            // Video element tapped -> show native controls temporarily
            const video = e.target.closest("video");
            if (video) {
              video.controls = true;
              video.blur();
              clearTimeout(video._hideCtrlsTimer);
              video._hideCtrlsTimer = setTimeout(() => {
                video.controls = false;
              }, 3000);
              return;
            }
          });
        }

        // gallery swiper configuration
        const default_gallery_configuration = {
          autoHeight: autoHeightEnabled,
          threshold: 10,
          grabCursor: true,
          navigation: {
            nextEl: ".wt-slider__nav-next",
            prevEl: ".wt-slider__nav-prev",
          },
          scrollbar: {
            el: ".wt-slider__scrollbar",
            draggable: true,
          },
          pagination: {
            el: ".swiper-pagination",
            type: "fraction",
          },
          on: {
            afterInit: initVideoInteractions,
            slideChangeTransitionEnd: handleSlideVideos,
          },
        };

        const custom_gallery_configuration = JSON.parse(
          this.elements.gallery.querySelector("[data-swiper-configuration]")
            .innerHTML,
        );
        this.gallery_configuration = {
          ...default_gallery_configuration,
          ...custom_gallery_configuration,
        };

        const updateThumbsSwiperClasses = (swiperInstance) => {
          const container = swiperInstance.el;
          const cls = {
            beginning: "swiper-at-beginning",
            end: "swiper-at-end",
            locked: "swiper-locked",
            ready: "swiper-thumbs-ready",
          };

          container.classList.remove(
            cls.beginning,
            cls.end,
            cls.locked,
            cls.ready,
          );

          if (swiperInstance.isLocked) {
            container.classList.add(cls.locked);
            return;
          }

          container.classList.add(cls.ready);

          if (swiperInstance.isBeginning)
            container.classList.add(cls.beginning);
          if (swiperInstance.isEnd) container.classList.add(cls.end);
        };

        // gallery thumbs swiper configuration
        const default_thumbs_configuration = {
          grabCursor: true,
          spaceBetween: 8,
          slidesPerView: 4,
          freeMode: false,
          threshold: 5,
          direction: "horizontal",
          watchSlidesVisibility: true,
          watchSlidesProgress: true,
          watchOverflow: true,
          navigation: {
            nextEl: ".wt-slider__nav-next",
            prevEl: ".wt-slider__nav-prev",
          },
          on: {
            afterInit(swiper) {
              updateThumbsSwiperClasses(swiper);
            },
            lock: updateThumbsSwiperClasses,
            unlock: updateThumbsSwiperClasses,
            slideChange: updateThumbsSwiperClasses,
            observerUpdate: updateThumbsSwiperClasses,
            resize: updateThumbsSwiperClasses,
          },
        };

        const custom_thumbs_configuration = JSON.parse(
          this.elements.thumbs.querySelector("[data-swiper-configuration]")
            .innerHTML,
        );
        this.thumbs_configuration = {
          ...default_thumbs_configuration,
          ...custom_thumbs_configuration,
        };
        this.resolution = this.matchResolution();
      }

      initializeGallery() {
        window.addEventListener("resize", () => {
          this.updateSwiperAutoHeight(this.gallerySwiper);
        });

        switch (this.configuration.desktopLayout) {
          case "carousel-vertical":
          case "carousel-horizontal":
            this.initializeCarouselGallery();
            break;
          case "masonry":
            window.addEventListener(
              "resize",
              function () {
                this.handleMassonry();
              }.bind(this),
            );
            this.handleMassonry();
            break;
          case "collage":
            window.addEventListener(
              "resize",
              function () {
                this.handleCollage();
              }.bind(this),
            );
            this.handleCollage();
            break;
        }
      }

      initializeCarouselGallery() {
        if (this.thumbsSwiper == null) {
          this.decorateSwiper(
            this.elements.thumbs,
            "wt-slider__container--thumbs",
          );
          this.thumbsSwiper = this.swiperThumbsInitilize();
        }
        this.decorateSwiper(this.elements.gallery);
        this.swiperGalleryInitilize(this.thumbsSwiper);

        const disableTouchSlide =
          this.elements.gallery.querySelector(".disable-touch");
        if (disableTouchSlide) {
          disableTouchSlide.classList.add("swiper-no-swiping");
        }
      }

      swiperGalleryInitilize(thumbsSwiper) {
        if (this.gallerySwiper == null) {
          const thumbs_configuration = {
            thumbs: {
              swiper: thumbsSwiper,
            },
          };
          const desktopRatio = this.getAttribute("desktop-ratio");

          const useAlwaysAutoHeight =
            (this.configuration.desktopLayout === "carousel-vertical" &&
              desktopRatio === "original") ||
            this.configuration.desktopLayout === "carousel-horizontal";

          let autoHeightEnabled = useAlwaysAutoHeight
            ? true
            : window.innerWidth <= 900;
          if (thumbsSwiper)
            this.gallery_configuration = {
              ...this.gallery_configuration,
              ...thumbs_configuration,
              autoHeight: autoHeightEnabled,
            };
          this.gallerySwiper = new Swiper(
            this.elements.gallery,
            this.gallery_configuration,
          );
        }
      }

      swiperThumbsInitilize() {
        let autoHeightEnabled = window.innerWidth <= 900;
        this.thumbs_configuration = {
          ...this.thumbs_configuration,
          autoHeight: autoHeightEnabled,
        };
        const swiper = new Swiper(
          this.elements.thumbs,
          this.thumbs_configuration,
        );
        return swiper;
      }

      destroyCarouselGallery() {
        if (this.thumbsSwiper != null) {
          this.thumbsSwiper.destroy();
          this.thumbsSwiper = null;
          this.undecorateSwiper(
            this.elements.thumbs,
            "wt-slider__container--thumbs",
          );
        }
        if (this.gallerySwiper != null) {
          this.gallerySwiper.destroy();
          this.undecorateSwiper(this.elements.gallery);
          this.gallerySwiper = null;
        }
      }

      handleMassonry() {
        if (this.matchResolution() == "desktop") {
          this.destroyCarouselGallery();
          this.initializeMasonryGallery();
        } else {
          this.destroyMasonryGallery();
          this.initializeCarouselGallery();
        }
      }

      handleCollage() {
        const mode =
          this.matchResolution() === "desktop"
            ? "desktop-collage"
            : "mobile-carousel";

        if (this._galleryLayoutMode === mode) {
          this.gallerySwiper?.update();
          return;
        }

        const previousIndex = this.gallerySwiper?.activeIndex ?? 0;

        if (mode === "desktop-collage") {
          this.destroyCarouselGallery();
          this.destroyCollageGallery();
          this.initializeCollageCarouselGallery();
        } else {
          this.destroyCollageCarouselGallery();
          this.destroyCollageGallery();
          this.initializeCarouselGallery();
        }

        this._galleryLayoutMode = mode;
        if (this.gallerySwiper && previousIndex > 0) {
          const max = Math.max(0, (this.gallerySwiper.slides?.length || 1) - 1);
          this.gallerySwiper.slideTo(Math.min(previousIndex, max), 0);
        }
      }

      decorateSwiper(el, element_class) {
        el.classList.add("swiper", "wt-slider__container", element_class);
        el.querySelector("[data-swiper-container]")?.classList.add(
          "swiper-wrapper",
          "wt-slider__wrapper",
        );
        el.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.add("swiper-slide", "wt-slider__slide");
        });
        this.galleryUpdateEvent();
      }

      undecorateSwiper(el, element_class) {
        el.classList.remove("swiper", "wt-slider__container", element_class);
        el.querySelector("[data-swiper-container]").classList.remove(
          "swiper-wrapper",
          "wt-slider__wrapper",
        );
        el.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.remove("swiper-slide", "wt-slider__slide");
        });
      }

      decorateCollage(el, element_class) {
        el.classList.add(`wt-${element_class}`);
        el.querySelector("[data-swiper-container]").classList.add(
          `wt-${element_class}__wrapper`,
        );
        el.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.add(`wt-${element_class}__slide`);
        });
        this.galleryUpdateEvent();
      }

      undecorateCollage(el, element_class) {
        el.classList.remove(`wt-${element_class}`);
        el.querySelector("[data-swiper-container]").classList.remove(
          `wt-${element_class}__wrapper`,
        );
        el.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.remove(`wt-${element_class}__slide`);
        });
      }

      initializeCollageGallery() {
        this.decorateCollage(this.elements.gallery, "collage");
      }

      destroyCollageGallery() {
        this.undecorateCollage(this.elements.gallery, "collage");
      }

      initializeCollageCarouselGallery() {
        if (this.gallerySwiper != null) return;

        this.classList.add("wt-product__gallery--collage-carousel");
        this.decorateSwiper(this.elements.gallery);

        const galleryEl = this.elements.gallery;
        const nextEl = galleryEl.querySelector(":scope > .wt-slider__nav-next");
        const prevEl = galleryEl.querySelector(":scope > .wt-slider__nav-prev");

        const config = {
          ...this.gallery_configuration,
          slidesPerView: 2,
          slidesPerGroup: 1,
          spaceBetween: 8,
          watchOverflow: true,
          autoHeight: false,
          grabCursor: true,
          navigation: {
            nextEl: nextEl || galleryEl.querySelector(".wt-slider__nav-next"),
            prevEl: prevEl || galleryEl.querySelector(".wt-slider__nav-prev"),
          },
          scrollbar: false,
          pagination: false,
        };
        delete config.thumbs;

        this.gallerySwiper = new Swiper(galleryEl, config);
      }

      destroyCollageCarouselGallery() {
        this.classList.remove("wt-product__gallery--collage-carousel");
        if (this.gallerySwiper != null) {
          this.gallerySwiper.destroy();
          this.undecorateSwiper(this.elements.gallery);
          this.gallerySwiper = null;
        }
      }

      initializeMasonryGallery() {
        this.decorateCollage(this.elements.gallery, "masonry");
      }

      destroyMasonryGallery() {
        this.undecorateCollage(this.elements.gallery, "masonry");
      }

      sortSlides(slides, featured_media_id) {
        // Find the index of the slide with featured_media_id
        const featuredIndex = slides.findIndex((slide) => {
          const mediaId = slide.querySelector("img")
            ? Number(slide.querySelector("img").getAttribute("data-media-id"))
            : null;
          return mediaId === Number(featured_media_id);
        });

        // If a slide with featured_media_id is found, move it to the beginning of the array
        if (featuredIndex > -1) {
          const featuredSlide = slides[featuredIndex];
          slides.splice(featuredIndex, 1);
          slides.unshift(featuredSlide);
        }

        return slides;
      }

      // Plus-size image markers in alt — prefer [sizeplus] / [plus] (no #).
      static PLUS_ALT_PATTERN = /\[(?:sizeplus|plus(?:[_-]size)?|plussize|plus)\]/i;
      static LEGACY_PLUS_HASH_TAGS = new Set([
        "plus",
        "sizeplus",
        "plus-size",
        "plus_size",
        "plussize",
      ]);
      static PLUS_SIZE_MIN = 16;

      getSlideMedia(slide) {
        return slide?.querySelector("img") || slide?.querySelector("video") || null;
      }

      altHasPlusTag(alt) {
        if (!alt) return false;
        if (GallerySection.PLUS_ALT_PATTERN.test(alt)) return true;
        if (alt.indexOf("#") === -1) return false;
        const tags = alt.match(/#[^\s#]+/g);
        if (!tags) return false;
        return tags.some((hashtag) => {
          const part = hashtag.slice(1).toLowerCase().trim();
          return part === "plus" || part === "sizeplus" || part.startsWith("plus");
        });
      }

      slideIsPlusMarked(slide) {
        if (!slide) return false;
        if (slide.getAttribute("data-plus-size") === "true") return true;
        const media = this.getSlideMedia(slide);
        const alt =
          media?.getAttribute("alt") ||
          slide.getAttribute("data-alt") ||
          "";
        return this.altHasPlusTag(alt);
      }

      isPlusTaggedSlide(slide) {
        return this.slideIsPlusMarked(slide);
      }

      parseSizeNumber(value) {
        if (value == null || value === "") return null;
        const match = String(value).trim().match(/(\d+)/);
        return match ? Number(match[1]) : null;
      }

      isSizeOptionLabel(label) {
        const normalized = String(label || "")
          .replace(/:\s*$/, "")
          .trim()
          .toLowerCase();
        return (
          normalized === "size" ||
          normalized === "us size" ||
          normalized === "uk size"
        );
      }

      getSizeOptionValue() {
        const sectionId = this.id?.replace(/^MediaGallery-/, "");
        if (!sectionId) return null;

        const variantOptions = document.querySelector(
          `variant-options[data-section="${sectionId}"]`,
        );
        if (!variantOptions) return null;

        const fieldsets = variantOptions.querySelectorAll(".wt-product__option");
        for (const fieldset of fieldsets) {
          const label =
            fieldset.querySelector(".wt-product__option__title .label")
              ?.textContent || "";
          if (!this.isSizeOptionLabel(label)) continue;

          const checkedInput = fieldset.querySelector(
            'input[type="radio"]:checked',
          );
          if (checkedInput?.value) return checkedInput.value;

          const nativeSelect = fieldset.querySelector(".js-variant-native-select");
          if (nativeSelect?.value) return nativeSelect.value;

          return "";
        }
        return null;
      }

      isPlusSizeSelected(options = []) {
        let sizeValue = this.getSizeOptionValue();
        // Single-option products (size only): fall back to the lone option value.
        if (sizeValue == null && options.length === 1) {
          sizeValue = options[0];
        }
        if (sizeValue == null || sizeValue === "") return false;
        const size = this.parseSizeNumber(sizeValue);
        return size != null && size >= GallerySection.PLUS_SIZE_MIN;
      }

      isAdPlusGalleryContext() {
        if (typeof window.ShopContext !== "undefined") {
          if (typeof window.ShopContext.isAdPlusGallery === "function") {
            return window.ShopContext.isAdPlusGallery();
          }
        }
        if (document.documentElement.dataset.shopSizeMode !== "plus") return false;
        try {
          return !new URLSearchParams(window.location.search).has("variant");
        } catch (e) {
          return true;
        }
      }

      isPreferredPlusSlide(slide, colorValues, allColorOptions) {
        return this.isPlusTaggedSlide(slide) && (
          colorValues.length === 0 ||
          this.slideHasColorMatch(slide, colorValues, allColorOptions, true)
        );
      }

      promotePlusSlides(slides, colorValues = this.getSelectedColorValues(), allColorOptions = this.getAllColorOptionValues()) {
        const plusSlides = [];
        const otherSlides = [];
        for (let i = 0; i < slides.length; i++) {
          const slide = slides[i];
          if (this.isPreferredPlusSlide(slide, colorValues, allColorOptions)) plusSlides.push(slide);
          else otherSlides.push(slide);
        }
        if (plusSlides.length === 0) return slides;
        return plusSlides.concat(otherSlides);
      }

      isColorOptionFieldset(fieldset) {
        const bodyClass =
          fieldset.querySelector('[class*="wt-product__option__body--"]')
            ?.className || "";

        if (/wt-product__option__body--(?:swatches|thumbs)/.test(bodyClass)) {
          return true;
        }

        const label = fieldset
          .querySelector(".wt-product__option__title .label")
          ?.textContent?.replace(/:\s*$/, "")
          .trim()
          .toLowerCase();

        return ["color", "colour", "couleur", "farbe", "colori"].includes(
          label,
        );
      }

      getColorOptionFieldsets() {
        const sectionId = this.id?.replace(/^MediaGallery-/, "");
        if (!sectionId) return [];

        const variantOptions = document.querySelector(
          `variant-options[data-section="${sectionId}"]`,
        );
        if (!variantOptions) return [];

        return Array.from(
          variantOptions.querySelectorAll(".wt-product__option"),
        ).filter((fieldset) => this.isColorOptionFieldset(fieldset));
      }

      getAllColorOptionValues() {
        return this.getColorOptionFieldsets()
          .flatMap((fieldset) =>
            Array.from(fieldset.querySelectorAll("input")).map(
              (radio) => radio.value,
            ),
          )
          .filter(Boolean);
      }

      getSelectedColorValues() {
        return this.getColorOptionFieldsets()
          .map(
            (fieldset) =>
              Array.from(fieldset.querySelectorAll("input")).find(
                (radio) => radio.checked,
              )?.value,
          )
          .filter(Boolean);
      }

      escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      }

      altMatchesColorPhrase(alt, colorValue) {
        if (!alt || !colorValue) return false;

        const pattern = new RegExp(
          `\\b${this.escapeRegExp(colorValue.trim())}\\b`,
          "i",
        );

        return pattern.test(alt);
      }

      getMatchingColorFromAlt(alt, colorOptions) {
        if (!alt || !colorOptions?.length) return null;

        const sortedColors = [...new Set(colorOptions)]
          .filter(Boolean)
          .sort((a, b) => b.length - a.length || a.localeCompare(b));

        return (
          sortedColors.find((color) => this.altMatchesColorPhrase(alt, color)) ||
          null
        );
      }

      getSlideAlt(slide) {
        const media = this.getSlideMedia(slide);
        return (
          media?.getAttribute("alt") ||
          slide?.getAttribute("data-alt") ||
          ""
        );
      }

      getSlideMediaId(slide) {
        const media = this.getSlideMedia(slide);
        const raw =
          media?.getAttribute("data-media-id") ||
          slide?.getAttribute("data-media-id") ||
          slide?.getAttribute("data-slide-media-id");
        return raw ? Number(raw) : null;
      }

      slideHasColorMatch(slide, colorValues, allColorOptions, includePlus = false) {
        if (!colorValues.length || (!includePlus && this.isPlusTaggedSlide(slide))) return false;

        const alt = this.getSlideAlt(slide);
        if (!alt) return false;

        const colorOptions = allColorOptions?.length
          ? allColorOptions
          : colorValues;
        const matchedColor = this.getMatchingColorFromAlt(alt, colorOptions);
        if (!matchedColor) return false;

        const normalizedMatched = matchedColor.toLowerCase().trim();
        return colorValues.some(
          (color) => color.toLowerCase().trim() === normalizedMatched,
        );
      }

      getSlideSortTier(slide, colorValues, featuredId, allColorOptions) {
        const mediaId = this.getSlideMediaId(slide);

        if (featuredId && mediaId === featuredId) return 0;
        if (this.slideHasColorMatch(slide, colorValues, allColorOptions)) return 1;
        return 2;
      }

      sortSlidesByColorOption(slides, featured_media_id) {
        const colorValues = this.getSelectedColorValues();
        const allColorOptions = this.getAllColorOptionValues();
        const hasColorMatches =
          colorValues.length > 0 &&
          slides.some((slide) =>
            this.slideHasColorMatch(slide, colorValues, allColorOptions),
          );

        if (!hasColorMatches) {
          if (featured_media_id) {
            return this.sortSlides([...slides], featured_media_id);
          }
          return slides;
        }

        const featuredId = featured_media_id ? Number(featured_media_id) : null;

        return slides
          .map((slide, index) => ({
            slide,
            index,
            tier: this.getSlideSortTier(
              slide,
              colorValues,
              featuredId,
              allColorOptions,
            ),
          }))
          .sort((a, b) => a.tier - b.tier || a.index - b.index)
          .map(({ slide }) => slide);
      }

      beforeGalleryChange() {
        this.classList.add("loading");
        this.galleryLoader?.classList.remove("hidden");
        this.style.minHeight = `${this.offsetHeight}px`;
      }

      afterGalleryChange() {
        this.style.minHeight = "unset";
        this.galleryLoader?.classList.add("hidden");
        this.classList.remove("loading");
      }

      filterSlidesByOptions(
        slides,
        options,
        featured_media_id,
        matchAll = true,
      ) {
        const lowercaseOptions = options.map((option) =>
          option.toLowerCase().replace(/\s/g, ""),
        );
        const featuredId =
          featured_media_id == null || featured_media_id === false
            ? null
            : String(featured_media_id);

        return slides.filter((slide) => {
          const media = this.getSlideMedia(slide);
          const alt =
            (media ? media.getAttribute("alt") : "") ||
            slide.getAttribute("data-alt") ||
            "";
          const mediaId =
            (media ? media.getAttribute("data-media-id") : "") ||
            slide.getAttribute("data-media-id") ||
            "";

          if (featuredId && String(mediaId) === featuredId) return true;

          // Plus-size media always visible; size >= 16 only reorders.
          if (mediaId && this.plusMediaIds?.has(String(mediaId))) return true;
          if (this.slideIsPlusMarked(slide)) return true;

          // Untagged media stays visible — skip regex work.
          if (!alt || alt.indexOf("#") === -1) return true;

          const altHashtags = (alt.match(/#[^\s#]+/g) || []).map((hashtag) =>
            hashtag.slice(1).toLowerCase(),
          );

          // Legacy #plus tags only — new format uses [sizeplus] and skips this filter.
          const optionHashtags = altHashtags.filter((tag) => {
            const variants = tag.split("|").map((part) => part.trim());
            return !variants.every((part) =>
              GallerySection.LEGACY_PLUS_HASH_TAGS.has(part),
            );
          });

          if (optionHashtags.length === 0) return true;
          if (optionHashtags.some((tag) => tag.split("|").includes("all")))
            return true;

          if (matchAll) {
            return optionHashtags.every((tag) =>
              tag
                .split("|")
                .some((variant) => lowercaseOptions.includes(variant.trim())),
            );
          }
          return optionHashtags.some((tag) =>
            tag
              .split("|")
              .some((variant) => lowercaseOptions.includes(variant.trim())),
          );
        });
      }

      galleryUpdateEvent(opt) {
        const galleryEvent = new CustomEvent("gallery:updated", {
          bubbles: true,
          cancelable: true,
          detail: {
            desc: "gallery updated",
            selector: ".wt-product__gallery",
            ...opt,
          },
        });
        document.dispatchEvent(galleryEvent);
      }

      filterSlides(options, featured_media_id, matchAll = true, callback) {
        if (!this.elements) return;
        const originalGallerySlides = Array.from(
          this.elements.gallerySlides,
          (el) => el.cloneNode(true),
        );
        const originalThumbsSlides = Array.from(
          this.elements.thumbsSlides,
          (el) => el.cloneNode(true),
        );

        const getMediaId = (item) =>
          item?.dataset?.mediaId ||
          this.getSlideMedia(item)?.getAttribute("data-media-id");

        let filteredGallerySlides = this.filterSlidesByOptions(
          originalGallerySlides,
          options,
          featured_media_id,
          matchAll,
        );
        let filteredThumbsSlides = this.filterSlidesByOptions(
          originalThumbsSlides,
          options,
          featured_media_id,
          matchAll,
        );

        if (filteredGallerySlides.length === 0) {
          filteredGallerySlides = [...originalGallerySlides];
          filteredThumbsSlides = [...originalThumbsSlides];
        }

        // Plus reorder when size >= 16, or ad landing ?size_mode=plus (no ?variant=).
        const plusSizeSelected =
          this.isAdPlusGalleryContext() || this.isPlusSizeSelected(options);
        const colorValues = this.getSelectedColorValues();
        const allColorOptions = this.getAllColorOptionValues();
        const hasMatchingPlusInGallery = filteredGallerySlides.some((slide) =>
          this.isPreferredPlusSlide(slide, colorValues, allColorOptions),
        );

        if (plusSizeSelected && hasMatchingPlusInGallery) {
          filteredGallerySlides = this.promotePlusSlides(filteredGallerySlides, colorValues, allColorOptions);
          filteredThumbsSlides = this.promotePlusSlides(filteredThumbsSlides, colorValues, allColorOptions);
        } else {
          // With no plus photo for this color, prefer its ordinary photos even
          // if a different-color plus photo was assigned as the variant image.
          const fallbackFeaturedId = plusSizeSelected && filteredGallerySlides.some(
            (slide) => this.getSlideMediaId(slide) === Number(featured_media_id) && this.isPlusTaggedSlide(slide),
          ) ? null : featured_media_id;
          filteredGallerySlides = this.sortSlidesByColorOption(
            filteredGallerySlides,
            fallbackFeaturedId,
          );
          filteredThumbsSlides = this.sortSlidesByColorOption(
            filteredThumbsSlides,
            fallbackFeaturedId,
          );
        }

        const plusPromoted = plusSizeSelected && hasMatchingPlusInGallery;
        this.dataset.plusPromoted = plusPromoted ? "true" : "false";
        if (typeof window.syncSizeAndFit === "function") {
          window.syncSizeAndFit();
        }

        const renderedSlidesChanged =
          this.renderedSlides.map(getMediaId).toString() !==
          filteredGallerySlides.map(getMediaId).toString();

        if (this.gallerySwiper && this.thumbsSwiper && renderedSlidesChanged) {
          this.beforeGalleryChange();
          const thumbSlidesWrapper = this.querySelector(
            "[data-thumbs] [data-swiper-container]",
          );
          const gallerySlidesWrapper = this.querySelector(
            "[data-gallery] [data-swiper-container]",
          );

          // Assuming this.gallerySwiper is the swiper instance for the main gallery
          this.gallerySwiper.removeAllSlides();
          gallerySlidesWrapper.innerHTML = "";
          const swiperContainer = this.querySelector(".swiper-wrapper");
          filteredGallerySlides.forEach((slide, idx) => {
            if (idx === 0) {
              const slideImg = slide.querySelector("img");
              if (slideImg)
                slideImg.onload = function () {
                  swiperContainer.style.height = "auto";
                };
            }
            this.gallerySwiper.appendSlide(slide);
          });
          this.decorateSwiper(this.elements.gallery);

          // Assuming this.thumbsSwiper is the swiper instance for the thumbnails
          this.thumbsSwiper.removeAllSlides();
          thumbSlidesWrapper.innerHTML = "";

          filteredThumbsSlides.forEach((slide) =>
            this.thumbsSwiper.appendSlide(slide),
          );

          this.decorateSwiper(
            this.elements.thumbs,
            "wt-slider__container--thumbs",
          );

          this.thumbsSwiper.update();
          this.gallerySwiper.update();
        } else if (
          this.configuration.desktopLayout == "collage" ||
          this.configuration.desktopLayout == "masonry"
        ) {
          if (renderedSlidesChanged) {
            this.beforeGalleryChange();

            const isDesktopCollageCarousel =
              this.configuration.desktopLayout == "collage" &&
              this.matchResolution() == "desktop" &&
              this.gallerySwiper != null;

            this.elements.thumbs
              .querySelectorAll("[data-swiper-slide]")
              .forEach((slide) => slide.remove());

            if (isDesktopCollageCarousel) {
              this.gallerySwiper.removeAllSlides();
              this.gallerySwiper.appendSlide(filteredGallerySlides);
              filteredThumbsSlides.forEach((slide) =>
                this.elements.thumbs
                  .querySelector("[data-swiper-container]")
                  .append(slide),
              );
              this.decorateSwiper(this.elements.gallery);
              this.gallerySwiper.update();
            } else {
              this.elements.gallery
                .querySelectorAll("[data-swiper-slide]")
                .forEach((slide) => slide.remove());

              filteredGallerySlides.forEach((slide) =>
                this.elements.gallery
                  .querySelector("[data-swiper-container]")
                  .append(slide),
              );
              filteredThumbsSlides.forEach((slide) =>
                this.elements.thumbs
                  .querySelector("[data-swiper-container]")
                  .append(slide),
              );

              this.decorateCollage(
                this.elements.gallery,
                this.configuration.desktopLayout,
              );
            }

            // add wt-product__gallery --even --odd classes to MediaGallery container depends on number of slides
            this.elements.gallery.classList.remove("wt-product__gallery--even");
            this.elements.gallery.classList.remove("wt-product__gallery--odd");
            if (filteredGallerySlides.length % 2 == 0) {
              this.elements.gallery.classList.add("wt-product__gallery--even");
            } else {
              this.elements.gallery.classList.add("wt-product__gallery--odd");
            }
          }
        }

        const firstMediaId = this.getSlideMediaId(filteredGallerySlides[0]);
        const activeMediaId = plusSizeSelected
          ? firstMediaId
          : featured_media_id || firstMediaId;
        if (activeMediaId) this.setActiveMedia(activeMediaId, true);
        this.gallerySwiper?.update();

        if (renderedSlidesChanged) {
          setTimeout(this.afterGalleryChange.bind(this), 300);
        }

        this.renderedSlides = [...filteredGallerySlides];
      }

      setActiveMedia(mediaId, prepend) {
        let media = this.elements.gallery.querySelector(
          `[data-media-id="${mediaId}"]`,
        );

        if (this.gallerySwiper != null) {
          this.gallerySwiper.slideTo(this.indexInParent(media));
          this.thumbsSwiper?.slideTo(this.indexInParent(media));
        }
      }

      matchResolution() {
        if (window.innerWidth < this.configuration.sliderEnabledBreakpoint) {
          return "mobile";
        } else {
          return "desktop";
        }
      }

      indexInParent(node) {
        if (!node) return -1;
        let children = node.parentNode.childNodes;
        let num = 0;
        for (let i = 0; i < children.length; i++) {
          if (children[i] == node) return num;
          if (children[i].nodeType == 1) num++;
        }
        return -1;
      }
    },
  );
}
